import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_SITE } from "src/constants/constants";
import {
  ChatState,
  ConfirmationLoading,
  FavoriteItem,
  ToolCall,
  StreamingHistory,
  MessageSegment,
  SourceAttribution,
  StreamingCompletion,
} from "./types";
import type { ChatResponse } from "./types";
import { DocumentInfo } from "src/components/shared/sourcesDropDown/types";

/** Message segment type constants for streaming */
const MESSAGE_SEGMENT_TYPES = {
  TEXT: "text" as const,
  TOOL_CALL: "tool_call" as const,
};

/** ID prefixes for generating unique identifiers for streaming */
const ID_PREFIXES = {
  TEXT: "text",
  STREAMING_TEXT: "streaming-text",
  TOOL: "tool",
  FINAL_TEXT: "final-text",
};

/** Generate unique ID with timestamp and random component for streaming */
const generateId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random()}`;

const initialState: ChatState = {
  inputText: "",
  recentInput: "",
  isLoading: false,
  isInputDisabled: false,
  showResult: false,
  errorMessage: "",
  responses: [],
  conversationId: "",
  isRecentChatsLoaded: false,
  recentChats: [],
  site: DEFAULT_SITE,
  isConfirmationLoading: false,
  isFavoriteMessagesLoaded: false,
  confirmationLoadingMessageId: null,
  favoriteMessages: [],
  streaming: {
    isStreaming: false,
    isCompleted: false,
    currentMessage: "",
    toolCalls: [],
    messageSegments: [],
    messageId: undefined,
  },
  streamingHistory: [],
};

const { reducer, actions } = createSlice({
  name: "chatSlice",
  initialState,
  reducers: {
    /** Set current input text */
    setInputText: (state, action: PayloadAction<string>) => {
      state.inputText = action.payload;
    },
    /** Set recent input text */
    setRecentInput: (state, action: PayloadAction<string>) => {
      state.recentInput = action.payload;
    },
    /** Set loading state for sync operations */
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    /** Set input disabled state */
    setIsInputDisabled: (state, action: PayloadAction<boolean>) => {
      state.isInputDisabled = action.payload;
    },
    /** Set show result state */
    setShowResult: (state, action: PayloadAction<boolean>) => {
      state.showResult = action.payload;
    },
    /** Set error message */
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    /** Add sync chat response with CSV/chart data */
    setResponses: (state, action: PayloadAction<ChatResponse>) => {
      action.payload === null
        ? (state.responses = [])
        : (state.responses = [...state.responses, action.payload]);
    },
    /** Update existing sync response */
    updateResponses: (state, action) => {
      const { message_id, updates } = action.payload;
      const existingResponse = state.responses.find(
        (response) => response.messageId === message_id,
      );
      if (existingResponse) {
        Object.assign(existingResponse, updates);
      }
    },
    /** Set conversation ID */
    setConversationId: (state, action: PayloadAction<string>) => {
      state.conversationId = action.payload;
    },
    /** Set recent chats loaded state */
    setIsRecentChatsLoaded: (state, action: PayloadAction<boolean>) => {
      state.isRecentChatsLoaded = action.payload;
    },
    /** Set favorite messages loaded state */
    setIsFavoriteMessagesLoaded: (state, action: PayloadAction<boolean>) => {
      state.isFavoriteMessagesLoaded = action.payload;
    },
    /** Set all favorite messages */
    setAllFavoriteMessages: (state, action: PayloadAction<FavoriteItem[]>) => {
      state.favoriteMessages = action.payload;
    },
    /** Set recent chats list */
    setRecentChats: (state, action: PayloadAction<any[]>) => {
      state.recentChats = action.payload.map((message) => ({
        userText: message.user_text,
        messageId: message.message_id,
        timestamp: message.timestamp.toString(),
      }));
    },
    /** Set current site */
    setSite: (state, action: PayloadAction<string>) => {
      state.site = action.payload;
    },
    /** Set confirmation loading state */
    setConfirmationLoading: (
      state,
      action: PayloadAction<ConfirmationLoading>,
    ) => {
      state.isConfirmationLoading = action.payload.isLoading;
      state.confirmationLoadingMessageId = action.payload.messageId;
    },

    // ========================================================================
    // STREAMING ACTIONS (real-time chat streaming)
    // ========================================================================

    /** Initialize streaming session */
    startStreaming: (
      state,
      action: PayloadAction<{ messageId: string; userText: string }>,
    ) => {
      state.streaming.isStreaming = true;
      state.streaming.isCompleted = false;
      state.streaming.messageId = action.payload.messageId;
      state.streaming.currentMessage = "";
      state.streaming.toolCalls = [];
      state.streaming.messageSegments = [];
      state.isInputDisabled = true;
      state.showResult = true;
      state.recentInput = action.payload.userText;
    },

    updateStreamingMessage: (state, action: PayloadAction<string>) => {
      state.streaming.currentMessage += action.payload;
      const textSegment: MessageSegment = {
        type: MESSAGE_SEGMENT_TYPES.TEXT,
        content: action.payload,
        id: generateId(ID_PREFIXES.TEXT),
        timestamp: Date.now(),
      };
      state.streaming.messageSegments.push(textSegment);
    },

    /** Add text chunk to streaming message */
    addStreamingTextChunk: (state, action: PayloadAction<string>) => {
      const lastSegmentIndex = state.streaming.messageSegments.length - 1;
      const lastSegment = state.streaming.messageSegments[lastSegmentIndex];

      if (
        lastSegment &&
        lastSegment.type === MESSAGE_SEGMENT_TYPES.TEXT &&
        lastSegment.isStreaming
      ) {
        lastSegment.content += action.payload;
        lastSegment.timestamp = Date.now();
      } else {
        const textSegment: MessageSegment = {
          type: MESSAGE_SEGMENT_TYPES.TEXT,
          content: action.payload,
          id: generateId(ID_PREFIXES.STREAMING_TEXT),
          timestamp: Date.now(),
          isStreaming: true,
        };
        state.streaming.messageSegments.push(textSegment);
      }
      state.streaming.currentMessage += action.payload;
    },

    /** Mark current streaming text as complete */
    completeStreamingText: (state) => {
      const lastTextSegment = state.streaming.messageSegments
        .slice()
        .reverse()
        .find(
          (seg) => seg.type === MESSAGE_SEGMENT_TYPES.TEXT && seg.isStreaming,
        );

      if (lastTextSegment) {
        lastTextSegment.isStreaming = false;
      }
    },

    /** Add or update tool call in streaming */
    addToolCall: (state, action: PayloadAction<ToolCall>) => {
      const existingIndex = state.streaming.toolCalls.findIndex(
        (tc) => tc.tool_use_id === action.payload.tool_use_id,
      );
      if (existingIndex >= 0) {
        state.streaming.toolCalls[existingIndex] = action.payload;
      } else {
        state.streaming.toolCalls.push(action.payload);
        const toolSegment: MessageSegment = {
          type: MESSAGE_SEGMENT_TYPES.TOOL_CALL,
          content: "",
          toolCall: action.payload,
          id: `${ID_PREFIXES.TOOL}-${action.payload.tool_use_id}`,
          timestamp: Date.now(),
        };
        state.streaming.messageSegments.push(toolSegment);
      }
    },

    /** Update existing tool call in streaming */
    updateToolCall: (
      state,
      action: PayloadAction<Partial<ToolCall> & { tool_use_id: string }>,
    ) => {
      const toolCallIndex = state.streaming.toolCalls.findIndex(
        (tc) => tc.tool_use_id === action.payload.tool_use_id,
      );
      if (toolCallIndex >= 0) {
        state.streaming.toolCalls[toolCallIndex] = {
          ...state.streaming.toolCalls[toolCallIndex],
          ...action.payload,
        };
        const segmentIndex = state.streaming.messageSegments.findIndex(
          (seg) =>
            seg.type === MESSAGE_SEGMENT_TYPES.TOOL_CALL &&
            seg.toolCall?.tool_use_id === action.payload.tool_use_id,
        );
        if (
          segmentIndex >= 0 &&
          state.streaming.messageSegments[segmentIndex].toolCall
        ) {
          state.streaming.messageSegments[segmentIndex] = {
            ...state.streaming.messageSegments[segmentIndex],
            toolCall: {
              ...state.streaming.messageSegments[segmentIndex].toolCall!,
              ...action.payload,
            },
          };
        }
      }
    },

    /** Update streaming metadata including CSV fields */
    updateStreamingMetadata: (
      state,
      action: PayloadAction<{
        sourceAttributions?: SourceAttribution[];
        relatedDocuments?: DocumentInfo[];
        isEmptyData?: boolean;
        toolName?: string;
        csvPreview?: string;
        csvData?: string;
        showCsv?: boolean;
      }>,
    ) => {
      if (action.payload.sourceAttributions) {
        state.streaming.sourceAttributions = action.payload.sourceAttributions;
      }
      if (action.payload.relatedDocuments) {
        state.streaming.relatedDocuments = action.payload.relatedDocuments;
      }
      if (action.payload.isEmptyData !== undefined) {
        state.streaming.isEmptyData = action.payload.isEmptyData;
      }
      if (action.payload.toolName !== undefined) {
        state.streaming.toolName = action.payload.toolName;
      }
      if (action.payload.csvPreview !== undefined) {
        state.streaming.csvPreview = action.payload.csvPreview;
      }
      if (action.payload.csvData !== undefined) {
        state.streaming.csvData = action.payload.csvData;
      }
      if (action.payload.showCsv !== undefined) {
        state.streaming.showCsv = action.payload.showCsv;
      }
    },
    completeStreaming: (state, action: PayloadAction<StreamingCompletion>) => {
      state.streaming.isStreaming = false;
      state.streaming.isCompleted = false; // Don't keep completed state visible
      state.isInputDisabled = false;
      state.inputText = "";

      // Mark all tool calls as completed
      state.streaming.toolCalls.forEach((toolCall) => {
        if (toolCall.status === "running") {
          toolCall.status = "completed";
        }
      });

      // Update tool calls in message segments as well
      state.streaming.messageSegments.forEach((segment) => {
        if (
          segment.type === MESSAGE_SEGMENT_TYPES.TOOL_CALL &&
          segment.toolCall &&
          segment.toolCall.status === "running"
        ) {
          segment.toolCall.status = "completed";
        }
      });

      // Add final response message to streaming segments if we have llm_message
      if (action.payload.llmMessage) {
        const finalTextSegment: MessageSegment = {
          type: MESSAGE_SEGMENT_TYPES.TEXT,
          content: action.payload.llmMessage,
          id: generateId(ID_PREFIXES.FINAL_TEXT),
          timestamp: Date.now(),
          isStreaming: false,
        };
        state.streaming.messageSegments.push(finalTextSegment);
      }

      const streamingHistoryItem: StreamingHistory = {
        messageId: action.payload.messageId,
        userText: state.recentInput,
        currentMessage: action.payload.llmMessage,
        toolCalls: state.streaming.toolCalls,
        messageSegments: state.streaming.messageSegments,
        datetime: new Date().toISOString(),
        sourceAttributions: state.streaming.sourceAttributions,
        relatedDocuments: state.streaming.relatedDocuments,
        isEmptyData: state.streaming.isEmptyData,
        toolName: state.streaming.toolName,
        thumbsUpClicked: false,
        thumbsDownClicked: false,
        voteResponse: "",
        csvPreview: state.streaming.csvPreview,
        csvData: state.streaming.csvData,
        showCsv: state.streaming.showCsv,
      };

      state.streamingHistory.push(streamingHistoryItem);

      // Clear streaming state to prevent duplicate display
      state.streaming = {
        isStreaming: false,
        isCompleted: false,
        currentMessage: "",
        toolCalls: [],
        messageSegments: [],
        messageId: undefined,
      };
    },
    stopStreaming: (state) => {
      state.streaming = {
        isStreaming: false,
        isCompleted: false,
        currentMessage: "",
        toolCalls: [],
        messageSegments: [],
        messageId: undefined,
      };
      state.isInputDisabled = false;
    },
    clearStreamingHistory: (state) => {
      state.streamingHistory = [];
    },
    updateStreamingHistory: (state, action) => {
      const { message_id, updates } = action.payload;
      const existingHistoryItem = state.streamingHistory.find(
        (item) => item.messageId === message_id,
      );
      if (existingHistoryItem) {
        Object.assign(existingHistoryItem, updates);
      }
    },
  },
});

export const {
  setInputText,
  setRecentInput,
  setIsLoading,
  setIsInputDisabled,
  setShowResult,
  setErrorMessage,
  setResponses,
  updateResponses,
  setConversationId,
  setIsRecentChatsLoaded,
  setIsFavoriteMessagesLoaded,
  setRecentChats,
  setSite,
  setConfirmationLoading,
  setAllFavoriteMessages,
  // Streaming actions
  startStreaming,
  updateStreamingMessage,
  addStreamingTextChunk,
  completeStreamingText,
  addToolCall,
  updateToolCall,
  updateStreamingMetadata,
  completeStreaming,
  stopStreaming,
  clearStreamingHistory,
  updateStreamingHistory,
} = actions;

export default reducer;
